"use client";

import StepHeading from "@/app/components/molecules/StepHeading";
import FormCard from "@/app/components/atoms/FormCard";
import ConfirmedEmailCard from "@/app/components/atoms/ConfirmedEmailCard";
import DynamicForm from "@/app/components/organisms/DynamicForm";
import type { FormConfig } from "@/app/components/types";

const nameConfig: FormConfig = {
  id: "register-name-form",
  fields: [
    {
      name: "firstName",
      label: "First name",
      type: "text",
      placeholder: "First name",
      required: true,
      autoFocus: true,
      autoComplete: "given-name",
    },
    {
      name: "lastName",
      label: "Last name",
      type: "text",
      placeholder: "Last name",
      required: true,
      autoComplete: "family-name",
    },
  ],
  submitLabel: "Continue",
  ariaLabelledBy: "register-name-header",
};

interface RegisterNameStepProps {
  /** The email confirmed in a previous step. */
  email: string;
  /** Returns the user to the email entry step. */
  onEditEmail: () => void;
  /** Called with first and last name when the user submits. */
  onContinue: (firstName: string, lastName: string) => void;
  /** Whether a lookup is in progress. */
  loading?: boolean;
  /** Error message from the name lookup. */
  error?: string | null;
}

/**
 * @component RegisterNameStep
 * @description Name entry step shown when email + phone alone matches multiple
 * different Altru records. Collects first and last name to narrow the match
 * to a single constituent.
 */
export default function RegisterNameStep({
  email,
  onEditEmail,
  onContinue,
  loading,
  error,
}: RegisterNameStepProps) {
  const handleSubmit = (data: Record<string, string | boolean>) => {
    onContinue(
      String(data["firstName"] ?? "").trim(),
      String(data["lastName"] ?? "").trim(),
    );
  };

  return (
    <div className="flex flex-col gap-8">
      <StepHeading id="register-name-header" title="Enter your name to continue" />
      <p className="text-center text-base text-charcoal -mt-4">
        We found multiple accounts with that email and phone number. Please enter your name so we can find your record.
      </p>

      <div className="w-full max-w-md m-auto flex flex-col gap-6">
        <ConfirmedEmailCard email={email} onEdit={onEditEmail} />
        <FormCard>
          <DynamicForm
            config={nameConfig}
            onSubmit={handleSubmit}
            loading={loading}
          />
          {error && (
            <p role="alert" className="text-body-base text-destructive mt-2 text-center">
              {error}
            </p>
          )}
          <p className="text-center text-base text-charcoal">
            Already have an account?{" "}
            <a
              href="/login"
              className="underline hover:text-accent-green transition-colors"
            >
              Login here
            </a>
          </p>
        </FormCard>
      </div>
    </div>
  );
}
